import React, { useEffect, useMemo, useState } from "react";
import CrudService from "services/cruds-service";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";

import {
    Grid,
    Card,
    Box,
    Chip,
    Button,
    TextField,
    MenuItem,
    InputAdornment,
    CircularProgress,
    FormControl,
    InputLabel,
    Select, Autocomplete,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PublicIcon from "@mui/icons-material/Public";
import PeopleIcon from "@mui/icons-material/People";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";

import DataTable from "examples/Tables/DataTable";

import { Doughnut, Bar, Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    PointElement,
    LineElement,
    Title,
    Tooltip as ChartTooltip,
    Legend,
} from "chart.js";

import dayjs from "dayjs";
import "dayjs/locale/tr";
import isoWeek from "dayjs/plugin/isoWeek";
import advancedFormat from "dayjs/plugin/advancedFormat";
import {KPIHint} from "./KPIHint";
import {useNavigate} from "react-router-dom";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    PointElement,
    LineElement,
    Title,
    ChartTooltip,
    Legend
);

dayjs.extend(isoWeek);
dayjs.extend(advancedFormat);

// ----------------- FX (örnek) -----------------
const FX_RATES = { USD: 1, EUR: 1.1, TRY: 0.03, GBP: 1.25 };
const toUsd = (amount, currency) => {
    const a = Number(amount || 0);
    const r = FX_RATES[String(currency || "USD").toUpperCase()] || 1;
    return a * r;
};
const convert = (usdAmount, toCurrency) => {
    const r = FX_RATES[String(toCurrency || "USD").toUpperCase()] || 1;
    return usdAmount / r;
};

// ----------------- STATUS (home taxonomy) -----------------
const STATUS_META = {
    "Pending":     { label: "Pending",     color: "#FFB300" },
    "Sent":        { label: "Sent",        color: "#1E88E5" },
    "Secured":     { label: "Secured",     color: "#43A047" },
    "Lost":        { label: "Lost",        color: "#E53935" },
    "Closed":      { label: "Closed",      color: "#546E7A" },
};


const statusOptions = [
    { value: "Pending",      label: "Pending",      color: "#FFB300" }, // amber
    { value: "Sent",         label: "Sent",         color: "#1E88E5" }, // blue
    { value: "Secured",      label: "Secured",      color: "#43A047" }, // green
    { value: "Lost",         label: "Lost",         color: "#E53935" }, // red
    { value: "Closed",       label: "Closed",       color: "#546E7A" }, // grey-blue
];

function StatusCell({ row, onLocalUpdate }) {
    const [saving, setSaving] = React.useState(false);

    const currentOption =
        statusOptions.find((o) => o.value === row.status || o.label === row.status) ?? statusOptions[0];

    const handleChange = async (_, newValue) => {
        const nextStatus = newValue?.value ?? newValue?.label ?? "";
        if (!nextStatus || nextStatus === row.status) return;

        const prevStatus = row.status; // ✅ rollback için gerçek önceki değer

        // ✅ UI anında güncellensin
        onLocalUpdate(row.id, nextStatus);

        setSaving(true);
        try {
            await CrudService.updateQuotingTool({ status: nextStatus }, row.id);
            // İstersen burada response ile kesinleştirebilirsin (opsiyonel)
        } catch (e) {
            // ✅ hata olursa geri al
            onLocalUpdate(row.id, prevStatus);
            console.error(e);
        } finally {
            setSaving(false);
        }
    };

    const Dot = ({ color }) => (
        <span
            style={{
                display: "inline-block",
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: color || "#999",
                marginRight: 8,
                flexShrink: 0,
            }}
        />
    );

    return (
        <Autocomplete
            options={statusOptions}
            value={currentOption}
            onChange={handleChange}
            disableClearable
            size="small"
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
            renderOption={({ key: optionKey, ...props }, option) => (
                <li key={optionKey} {...props} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Dot color={option.color} />
                    <span>{option.label}</span>
                </li>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Status"
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                            <>
                                <Dot color={currentOption?.color} />
                                {params.InputProps.startAdornment}
                            </>
                        ),
                        endAdornment: (
                            <>
                                {saving ? <CircularProgress size={16} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
            sx={{ minWidth: 170 }}
        />
    );
}

const cleanStr = (v) => {
    if (v == null) return "";
    const s = String(v).trim();
    if (!s) return "";
    // bazen "Unknown" gibi değerler DB'ye düşebiliyor
    if (s.toLowerCase() === "unknown") return "";
    if (s.toLowerCase() === "unknown country") return "";
    return s;
};

const firstNonEmpty = (...vals) => {
    for (const v of vals) {
        const s = cleanStr(v);
        if (s) return s;
    }
    return "";
};

const getCarrierCountry = (a, key /* polCountry | podCountry */) => {
    const carriers = Array.isArray(a?.carriers) ? a.carriers : [];
    // ilk carrier, yoksa herhangi bir carrier
    const fromFirst = carriers?.[0]?.[key];
    const fromAny = carriers.find((c) => cleanStr(c?.[key]))?.[key];
    return firstNonEmpty(fromFirst, fromAny);
};


// Eski statülerden geliyorsa map
const normalizeStatus = (s) => {
    const low = String(s || "").trim().toLowerCase();
    if (low === "pending") return "Pending";
    if (low === "sent") return "Sent";
    if (low === "secured" || low === "won") return "Secured";
    if (low === "lost") return "Lost";
    if (low === "expired" || low === "closed") return "Closed";
    return "Pending";
};


const StatusDot = ({ status }) => {
    const meta = STATUS_META[status] || { color: "#999", label: status || "—" };

    return (
        <span
            style={{
                display: "inline-block",
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: meta.color,
                marginRight: 8,
                verticalAlign: "middle",
            }}
        />
    );
};

const alphaHex = (hex, opacity = 0.12) => {
    const h = (hex || "#000000").replace("#", "");
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    return `rgba(${r},${g},${b},${opacity})`;
};

const fmt2 = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

// ----------------- Chart options (senin sayfadaki gibi) -----------------
const chartOptions = (type = "doughnut", onClickHandler) => ({
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
        legend: {
            position: "bottom",
            fullSize: false,
            labels: { boxWidth: 14, padding: 12, font: { size: 12 } },
        },
    },
    layout: { padding: { bottom: 20 } },
    onClick: (event, elements, chart) => {
        if (!elements?.length) return;
        const idx = elements[0]?.index;
        const label = chart?.data?.labels?.[idx];
        if (label && onClickHandler) onClickHandler(String(label));
    },
    ...(type === "bar" && {
        scales: { x: { grid: { display: false } }, y: { grid: { color: "#eee" } } },
    }),
});

const KPI_HINTS = {
    totalQuotes: {
        title: "Total Quotes",
        sections: [
            { label: "Purpose", text: "Shows the total number of quotes based on the selected date range and active filters." },
            { label: "Formula", text: "Total quotes = analyticsRows.length" },
        ],
    },

    winRate: {
        title: "Win Rate",
        sections: [
            { label: "Purpose", text: "Shows the percentage of quotes that were won." },
            { label: "Formula", text: "Win Rate = (Secured quotes / Total quotes) × 100" },
            { label: "Note", text: "Updates instantly when date range or filter chips change." },
        ],
    },

    wonRevenue: {
        title: "Won Revenue",
        sections: [
            { label: "Purpose", text: "Shows the total selling amount for Secured (won) quotes only." },
            { label: "Formula", text: "Won Revenue = Σ selling (Secured quotes)" },
            { label: "Currency", text: "Displayed in the selected currency after conversion." },
        ],
    },

    totalGm: {
        title: "Total Profit",
        sections: [
            { label: "Purpose", text: "Shows the total gross profit for Secured (won) quotes only." },
            { label: "Formula", text: "Total Profit = Σ (selling − cost) (Secured quotes)" },
            { label: "Profit %", text: "Profit % = (Profit / Won Revenue) × 100" },
        ],
    },

    uniqueCustomers: {
        title: "Unique Customers",
        sections: [
            { label: "Purpose", text: "Shows how many distinct customers exist in the current filtered dataset." },
            { label: "Formula", text: "Unique Customers = count(distinct customer)" },
        ],
    },
};

// ----------------- KPI Card (GlobalAgentDashboard style) -----------------
const KPICard = ({ title, value, icon, trend, hintKey }) => {
    const hint = hintKey ? KPI_HINTS[hintKey] : null;

    return (
        <Card sx={{ p: 3, borderRadius: "10px", boxShadow: "none" }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                {icon}

                <Box display="flex" alignItems="center">
                    <MDTypography variant="h6" color="text">
                        {title}
                    </MDTypography>

                    {hint ? <KPIHint title={hint.title || title} sections={hint.sections} /> : null}
                </Box>
            </Box>

            <MDTypography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>
                {value}
            </MDTypography>

            {trend ? (
                <MDTypography variant="caption" color="text">
                    {trend}
                </MDTypography>
            ) : null}
        </Card>
    );
};

// ----------------- Chip color safety -----------------
const SAFE_CHIP_COLORS = new Set([
    "default",
    "primary",
    "secondary",
    "error",
    "info",
    "success",
    "warning",
]);
const safeChipColor = (c) => (SAFE_CHIP_COLORS.has(c) ? c : "default");

const chipColor = (type) => {
    switch (String(type || "").trim()) {
        case "status":
            return "secondary";
        case "product":
            return "error";
        case "origin":
            return "success";
        case "destination":
            return "warning";
        case "customer":
            return "info";
        case "owner":
            return "info";
        case "requestedMonth":
            return "primary";
        default:
            return "default";
    }
};

export default function QuotingIntelligenceHome({ embedded = false }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const [selectedCurrency, setSelectedCurrency] = useState("USD");
    const [globalSearch, setGlobalSearch] = useState("");
    const [chartFilters, setChartFilters] = useState([]);
    const [dateStart, setDateStart] = useState("");
    const [dateEnd, setDateEnd] = useState("");
    const handleLocalStatusUpdate = (id, newStatus) => {
        setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status: newStatus } : r)));
    };
    // ---- Fetch Quotes ----
    useEffect(() => {
        (async () => {
            try {
                // ✅ endpoint adın farklıysa burayı değiştir
                const res = await CrudService.getQuotingTools();
                const data = res?.data?.data || res?.data || [];

                const normalized = (Array.isArray(data) ? data : []).map((item) => {
                    const a = item?.attributes || item;

                    const currency = a?.currency || a?.baseCurrency || "USD";
                    const sell = Number(a?.sallingRate || a?.sell || 0);
                    const cost = Number(a?.totalFreightCost || a?.cost || 0);
                    const status = normalizeStatus(a?.status);
                    const service = a?.service || a?.serviceMode || a?.serviceType || "Unknown";

                    const product =
                        a?.selectedTransportType ||
                        a?.transportationMode ||
                        a?.serviceMode ||
                        a?.product ||
                        "Unknown";

                    // ✅ BURAYA EKLE
                    const createdSource =
                        a?.createdSource ||
                        a?.source ||
                        a?.creationType ||
                        (a?.prefillRefId ? "Connect" : "Manual") ||
                        "unknown";

                    const quoteDate = a?.quoteDate
                        ? new Date(a.quoteDate)
                        : a?.created_at
                            ? new Date(a.created_at)
                            : a?.createdAt
                                ? new Date(a.createdAt)
                                : null;

                    const originCountry = firstNonEmpty(
                        a?.pickupLocation?.country,
                        a?.originCountry,
                        a?.origin,
                        a?.polCountry,
                        getCarrierCountry(a, "polCountry"),
                        a?.carrier?.polCountry,
                        a?.carrier?.originCountry
                    );

                    const destinationCountry = firstNonEmpty(
                        a?.deliveryLocation?.country,
                        a?.destCountry,
                        a?.destinationCountry,
                        a?.destination,
                        a?.podCountry,
                        getCarrierCountry(a, "podCountry"),
                        a?.carrier?.podCountry,
                        a?.carrier?.destinationCountry
                    );

                    return {
                        id: item?.id || a?.id,
                        quoteNo: a?.quoteNo || a?.rfqNumber || "—",
                        customer: a?.customer?.name || a?.customer || "Unknown",
                        originCountry: originCountry || "—",
                        destinationCountry: destinationCountry || "—",
                        product,
                        status,
                        service,
                        createdSource,
                        currency,
                        sell,
                        cost,
                        sellUsd: toUsd(sell, currency),
                        costUsd: toUsd(cost, currency),
                        sellDb: sell,
                        costDb: cost,
                        currencyDb: currency,
                        gmDb: sell - cost,
                        gmPctDb: sell ? ((sell - cost) / sell) * 100 : 0,

                        quoteDate,
                        quoteDateStr: quoteDate ? dayjs(quoteDate).format("YYYY-MM-DD") : "",

                        salesRepIds: Array.isArray(a?.salesRep)
                            ? a.salesRep
                            : Array.isArray(a?.salesRepIds)
                                ? a.salesRepIds
                                : [],
                    };
                });

                setRows(normalized);
            } catch (e) {
                console.error("quotes fetch error:", e);
                setRows([]);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    // ---- Date filter helper ----
    const isWithinDateRange = (row) => {
        if (!dateStart && !dateEnd) return true;
        if (!row?.quoteDateStr) return false;

        const d = dayjs(row.quoteDateStr);
        if (dateStart && d.isBefore(dayjs(dateStart).startOf("day"))) return false;
        if (dateEnd && d.isAfter(dayjs(dateEnd).endOf("day"))) return false;
        return true;
    };

    // 1) date base
    const dateFilteredBase = useMemo(() => {
        return (rows || []).filter(isWithinDateRange);
    }, [rows, dateStart, dateEnd]);

    // 2) date + chartFilters (analytics)
    const analyticsRows = useMemo(() => {
        return (dateFilteredBase || []).filter((r) => {
            for (const f of chartFilters || []) {
                if (!f?.type || !f?.value) continue;

                if (f.type === "status" && r.status !== f.value) return false;
                if (f.type === "product" && r.product !== f.value) return false;
                if (f.type === "origin" && r.originCountry !== f.value) return false;
                if (f.type === "destination" && r.destinationCountry !== f.value) return false;
                if (f.type === "customer" && r.customer !== f.value) return false;

                if (f.type === "requestedMonth") {
                    if (!r.quoteDateStr) return false;
                    const m = dayjs(r.quoteDateStr).format("YYYY-MM");
                    if (m !== f.value) return false;
                }
                if (f.type === "requestedWeek") {
                    if (!r.quoteDateStr) return false;
                    const d = dayjs(r.quoteDateStr);
                    const wk = String(d.isoWeek?.() ?? d.week?.() ?? 0).padStart(2, "0");
                    const wy = d.isoWeekYear?.() ?? d.year();
                    const key = `${wy}-W${wk}`;
                    if (key !== f.value) return false;
                }
                if (f.type === "createdSource" && r.createdSource !== f.value) return false;
                if (f.type === "service" && r.service !== f.value) return false;

            }
            return true;
        });
    }, [dateFilteredBase, chartFilters]);

    const WON_STATUS = "Secured";

    const wonRows = useMemo(() => {
        return (analyticsRows || []).filter((r) => r.status === WON_STATUS);
    }, [analyticsRows]);

    // 3) table: analytics + search
    const tableRows = useMemo(() => {
        return (analyticsRows || []).filter((r) => {
            if (!globalSearch) return true;
            const q = globalSearch.trim().toLowerCase();

            return (
                String(r.quoteNo || "").toLowerCase().includes(q) ||
                String(r.customer || "").toLowerCase().includes(q) ||
                String(r.originCountry || "").toLowerCase().includes(q) ||
                String(r.destinationCountry || "").toLowerCase().includes(q) ||
                String(r.product || "").toLowerCase().includes(q) ||
                String(r.status || "").toLowerCase().includes(q) ||
                String(r.service || "").toLowerCase().includes(q)
            );
        });
    }, [analyticsRows, globalSearch]);

    // ---- Filter actions ----
    const addFilter = (type, value) => {
        const t = String(type ?? "").trim();
        const v = String(value ?? "").trim();
        if (!t || !v) return;

        setChartFilters((prev) => {
            const clean = Array.isArray(prev) ? prev.filter((x) => x?.type && x?.value) : [];
            if (clean.some((x) => x.type === t && x.value === v)) return clean;
            return [...clean, { type: t, value: v }];
        });
    };

    const removeFilter = (f) => {
        if (!f?.type || !f?.value) return;
        setChartFilters((prev) =>
            (Array.isArray(prev) ? prev : [])
                .filter((x) => x?.type && x?.value)
                .filter((x) => !(x.type === f.type && x.value === f.value))
        );
    };

    // ---- KPI (selectedCurrency in convert) ----
    const totals = useMemo(() => {
        const total = analyticsRows.length;

        const byStatus = Object.keys(STATUS_META).reduce((acc, k) => {
            acc[k] = analyticsRows.filter((r) => r.status === k).length;
            return acc;
        }, {});

        // Won totals
        const wonRows = analyticsRows.filter((r) => r.status === "Secured");
        const wonSellUsd = wonRows.reduce((s, r) => s + (r.sellUsd || 0), 0);
        const wonCostUsd = wonRows.reduce((s, r) => s + (r.costUsd || 0), 0);
        const wonGmUsd = wonSellUsd - wonCostUsd;
        const wonGmPct = wonSellUsd ? (wonGmUsd / wonSellUsd) * 100 : 0;

        const won = byStatus[WON_STATUS] || 0;
        const winRate = total ? Math.round((won / total) * 100) : 0;

        const wonSell = convert(wonSellUsd, selectedCurrency);
        const wonCost = convert(wonCostUsd, selectedCurrency);
        const wonGm = convert(wonGmUsd, selectedCurrency);

        // Unique metrics
        const uniqCustomers = new Set(analyticsRows.map((r) => r.customer).filter(Boolean)).size;
        const uniqOrigins = new Set(analyticsRows.map((r) => r.originCountry).filter(Boolean)).size;
        const uniqDests = new Set(analyticsRows.map((r) => r.destinationCountry).filter(Boolean)).size;

        return {
            total,
            byStatus,
            winRate,
            wonSell,
            wonCost,
            wonGm,
            wonGmPct,
            uniqCustomers,
            uniqOrigins,
            uniqDests,
        };
    }, [analyticsRows, selectedCurrency]);

    // ---- Status sums (sell) for right chips ----
    const statusOfferSums = useMemo(() => {
        const sums = {};
        Object.keys(STATUS_META).forEach((k) => (sums[k] = 0));

        analyticsRows.forEach((r) => {
            const usd = Number(r.sellUsd || 0);
            const converted = convert(usd, selectedCurrency);
            sums[r.status] = (sums[r.status] || 0) + converted;
        });

        return sums;
    }, [analyticsRows, selectedCurrency]);

    // ---- Charts data ----
    const rfqsByStatusData = useMemo(() => {
        const labels = Object.keys(STATUS_META);
        const counts = labels.map((s) => analyticsRows.filter((r) => r.status === s).length);

        return {
            labels,
            datasets: [
                {
                    label: "RFQs",
                    data: counts,
                    backgroundColor: labels.map((s) => STATUS_META[s].color),
                    borderWidth: 2,
                },
            ],
        };
    }, [analyticsRows]);

    const topOrigins = useMemo(() => {
        const map = {};
        analyticsRows.forEach((r) => {
            const k = r.originCountry || "Unknown";
            map[k] = (map[k] || 0) + 1;
        });
        return Object.entries(map)
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 6);
    }, [analyticsRows]);

    const topDestinations = useMemo(() => {
        const map = {};
        analyticsRows.forEach((r) => {
            const k = r.destinationCountry || "Unknown";
            map[k] = (map[k] || 0) + 1;
        });
        return Object.entries(map)
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 6);
    }, [analyticsRows]);

    const topCustomers = useMemo(() => {
        const map = {};
        analyticsRows.forEach((r) => {
            const k = r.customer || "Unknown";
            map[k] = (map[k] || 0) + 1;
        });
        return Object.entries(map)
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 6);
    }, [analyticsRows]);

    const requestedMonths = useMemo(() => {
        const map = {};
        analyticsRows.forEach((r) => {
            if (!r.quoteDateStr) return;
            const m = dayjs(r.quoteDateStr).format("YYYY-MM");
            map[m] = (map[m] || 0) + 1;
        });
        return Object.entries(map)
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [analyticsRows]);

    const monthlyTotals = useMemo(() => {
        const map = {}; // { "YYYY-MM": { sellUsd, costUsd, count } }

        analyticsRows.forEach((r) => {
            if (!r.quoteDateStr) return;
            const key = dayjs(r.quoteDateStr).format("YYYY-MM");

            if (!map[key]) map[key] = { sellUsd: 0, costUsd: 0, count: 0 };

            map[key].sellUsd += Number(r.sellUsd || 0);
            map[key].costUsd += Number(r.costUsd || 0);
            map[key].count += 1;
        });

        return Object.entries(map)
            .map(([label, v]) => ({
                label,
                count: v.count,
                sell: convert(v.sellUsd, selectedCurrency),
                cost: convert(v.costUsd, selectedCurrency),
                profit: convert(v.sellUsd - v.costUsd, selectedCurrency),
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [analyticsRows, selectedCurrency]);

    const topServices = useMemo(() => {
        const map = {};
        analyticsRows.forEach((r) => {
            const k = (r.service || "Unknown").trim();
            map[k] = (map[k] || 0) + 1;
        });

        return Object.entries(map)
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 8);
    }, [analyticsRows]);

    const rfqsByServiceData = useMemo(() => {
        const labels = topServices.map((x) => x.label);
        const counts = topServices.map((x) => x.count);

        return {
            labels,
            datasets: [
                {
                    label: "Quotes",
                    data: counts,
                    borderWidth: 2,
                },
            ],
        };
    }, [topServices]);


    const requestedWeeks = useMemo(() => {
        const map = {};
        analyticsRows.forEach((r) => {
            if (!r.quoteDateStr) return;
            const d = dayjs(r.quoteDateStr);
            const wk = String(d.isoWeek?.() ?? d.week?.() ?? 0).padStart(2, "0");
            const wy = d.isoWeekYear?.() ?? d.year();
            const key = `${wy}-W${wk}`; // ör: 2026-W06
            map[key] = (map[key] || 0) + 1;
        });

        return Object.entries(map)
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [analyticsRows]);

    const prefillManualStats = useMemo(() => {
        const map = { prefill: 0, manual: 0, unknown: 0 };
        analyticsRows.forEach((r) => {
            const k = String(r.createdSource || "unknown").toLowerCase();
            if (k === "prefill") map.prefill += 1;
            else if (k === "manual") map.manual += 1;
            else map.unknown += 1;
        });
        const total = map.prefill + map.manual + map.unknown;
        return { ...map, total };
    }, [analyticsRows]);

    const createdSourceChartData = useMemo(() => ({
        labels: ["Connect", "Manual"],
        datasets: [
            { label: "Quotes", data: [prefillManualStats.prefill, prefillManualStats.manual, prefillManualStats.unknown], borderWidth: 2 },
        ],
    }), [prefillManualStats]);


    // ---- Top Transport Types (selectedTransportType) ----
    const topTransportTypes = useMemo(() => {
        const map = {};
        analyticsRows.forEach((r) => {
            const k = (r.product || "Unknown").trim();
            map[k] = (map[k] || 0) + 1;
        });

        return Object.entries(map)
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 8);
    }, [analyticsRows]);

    const clickEditHandler = (id) => {
        navigate(`/quoting-tool/create-quoting-tool/details?id=${id}`); // ID'yi query parametre olarak gönderiyoruz
    };

    const rfqsByTransportTypeData = useMemo(() => {
        const labels = topTransportTypes.map((x) => x.label);
        const counts = topTransportTypes.map((x) => x.count);

        return {
            labels,
            datasets: [
                {
                    label: "Quotes",
                    data: counts,
                    borderWidth: 2,
                },
            ],
        };
    }, [topTransportTypes]);


    // ---- DataTable columns (senin style standardına uygun) ----
    const columns = useMemo(
        () => [
            {
                Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Quote No</span>,
                accessor: "quoteNo",
                Cell: ({ row }) => (
                    <span
                        style={{
                            color: "blue",
                            textDecoration: "underline",
                            cursor: "pointer",
                            fontWeight: "bold",
                        }}
                        onClick={() => clickEditHandler(row.original.id)}
                    >
                    {row.original.quoteNo}
                </span>
                ),
            },
            {
                Header: "Customer",
                accessor: "customer",
                Cell: ({ value }) => {
                    const full = String(value || "");
                    const short = full.length > 20 ? full.slice(0, 20) + "…" : full;

                    return (
                        <span title={full} style={{ cursor: "help" }}>
        {short}
      </span>
                    );
                },
            },
            { Header: "Product", accessor: "product" },
            { Header: "Origin", accessor: "originCountry" },
            { Header: "Destination", accessor: "destinationCountry" },
            {
                Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Status</span>,
                accessor: "status",
                Cell: ({ row }) => (
                    <StatusCell
                        row={row.original}
                        onLocalUpdate={handleLocalStatusUpdate}
                    />
                ),
            },
            {
                Header: "Cost",
                accessor: "costDb",
                Cell: ({ row }) => `${fmt2(row.original.costDb)} ${row.original.currencyDb || ""}`,
            },
            {
                Header: "Sell",
                accessor: "sellDb",
                Cell: ({ row }) => `${fmt2(row.original.sellDb)} ${row.original.currencyDb || ""}`,
            },
            {
                Header: "Date",
                accessor: "quoteDateStr",
                Cell: ({ value }) => (value ? dayjs(value).locale("tr").format("DD/MM/YYYY") : "—"),
            },
        ],
        [selectedCurrency]
    );

    const revenueGmTrendWeekly = useMemo(() => {
        const map = {}; // "YYYY-Wxx" -> { sell, cost }

        wonRows.forEach((r) => {
            if (!r.quoteDateStr) return;
            const d = dayjs(r.quoteDateStr);
            const wk = String(d.isoWeek()).padStart(2, "0");
            const wy = d.isoWeekYear();
            const key = `${wy}-W${wk}`;

            if (!map[key]) map[key] = { key, sell: 0, cost: 0 };

            map[key].sell += convert(r.sellUsd || 0, selectedCurrency);
            map[key].cost += convert(r.costUsd || 0, selectedCurrency);
        });

        return Object.values(map)
            .map((x) => {
                const gm = x.sell - x.cost;
                const gmPct = x.sell ? (gm / x.sell) * 100 : 0;
                return { ...x, gm, gmPct };
            })
            .sort((a, b) => a.key.localeCompare(b.key));
    }, [wonRows, selectedCurrency]);

    const gmErosionAlert = useMemo(() => {
        const t = revenueGmTrendWeekly;
        if (t.length < 3) return null;

        const last = t[t.length - 1];
        const prev = t[t.length - 2];

        const revenueUp = last.sell > prev.sell;
        const gmPctDown = last.gmPct < prev.gmPct;

        if (revenueUp && gmPctDown) {
            return `GM erosion detected: Revenue up, GM% down (${fmt2(prev.gmPct)}% → ${fmt2(last.gmPct)}%).`;
        }
        return null;
    }, [revenueGmTrendWeekly]);


    const content = (
        <MDBox py={embedded ? 0 : 3} px={embedded ? 0 : 2}>
            <MDBox py={3} px={2}>
                {loading ? (
                    <MDBox display="flex" justifyContent="center" alignItems="center" height="60vh">
                        <CircularProgress />
                    </MDBox>
                ) : (
                    <Grid container spacing={4}>
                        {/* KPI row (GlobalAgentDashboard style) */}
                        <Grid item xs={12}>
                            <Grid container spacing={4}>
                                <Grid item xs={12} sm={6} md={2.4}>
                                    <KPICard title="Total Quotes" value={totals.total} icon={<TrendingUpIcon fontSize="large" color="primary" />} hintKey="totalQuotes" />
                                </Grid>
                                <Grid item xs={12} sm={6} md={2.4}>
                                    <KPICard title="Win Rate" value={`${totals.winRate}%`} icon={<PublicIcon fontSize="large" color="success" />} hintKey="winRate"/>
                                </Grid>
                                <Grid item xs={12} sm={6} md={2.4}>
                                    <KPICard title={`Secured Revenue (${selectedCurrency})`} value={fmt2(totals.wonSell)} icon={<CurrencyExchangeIcon fontSize="large" color="info" />} hintKey="wonRevenue" />
                                </Grid>
                                <Grid item xs={12} sm={6} md={2.4}>
                                    <KPICard title={`Total Profit (${selectedCurrency})`} value={fmt2(totals.wonGm)} icon={<TrendingUpIcon fontSize="large" color="secondary" />} hintKey="totalGm" />
                                </Grid>
                                <Grid item xs={12} sm={6} md={2.4}>
                                    <KPICard title="Unique Customers" value={totals.uniqCustomers} icon={<PeopleIcon fontSize="large" color="warning" />} hintKey="uniqueCustomers" />
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* RFQs by Status (donut + right chips) */}
                        <Grid item xs={12} md={4}>
                            <Card sx={{ p: 3, height: 350, borderRadius: "10px", boxShadow: "none" }}>
                                <MDTypography variant="h6" sx={{ mb: 2 }}>
                                    Quotes by Status
                                </MDTypography>

                                <Box sx={{ display: "flex", gap: 2, alignItems: "center", height: 290 }}>
                                    <Box sx={{ flex: 1, height: 260, minWidth: 0 }}>
                                        <Doughnut
                                            data={rfqsByStatusData}
                                            options={{
                                                ...chartOptions("doughnut", (label) => addFilter("status", label)),
                                                plugins: { legend: { display: false } },
                                            }}
                                        />
                                    </Box>

                                    <Box
                                        sx={{
                                            width: 210,
                                            height: 260,
                                            overflowY: "auto",
                                            pr: 0.5,
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 1,
                                        }}
                                    >
                                        {Object.keys(STATUS_META).map((key) => {
                                            const meta = STATUS_META[key];
                                            const sum = statusOfferSums?.[key] ?? 0;

                                            return (
                                                <Chip
                                                    key={key}
                                                    size="small"
                                                    onClick={() => addFilter("status", key)}
                                                    icon={<StatusDot status={key} />}
                                                    label={`${meta.label}: ${fmt2(sum)} ${selectedCurrency}`}
                                                    sx={{
                                                        borderRadius: 2,
                                                        fontWeight: 700,
                                                        cursor: "pointer",
                                                        justifyContent: "flex-start",
                                                        bgcolor: alphaHex(meta.color, 0.12),
                                                        color: meta.color,
                                                        "& .MuiChip-icon": { color: meta.color },
                                                        "& .MuiChip-label": { width: "100%", textAlign: "left" },
                                                    }}
                                                />
                                            );
                                        })}
                                    </Box>
                                </Box>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Card sx={{ p: 3, height: 350, borderRadius: "10px", boxShadow: "none" }}>
                                <MDTypography variant="h6" sx={{ mb: 2 }}>
                                    Quote Source
                                </MDTypography>

                                <Doughnut
                                    data={createdSourceChartData}
                                    options={chartOptions("doughnut", (label) => addFilter("createdSource", label))}
                                />
                            </Card>
                        </Grid>
                        {/* Top Transport Types (selectedTransportType) */}
                        <Grid item xs={12} md={4}>
                            <Card sx={{ p: 3, height: 350, borderRadius: "10px", boxShadow: "none" }}>
                                <MDTypography variant="h6" sx={{ mb: 2 }}>
                                    Top Transport Types
                                </MDTypography>

                                <Doughnut
                                    data={rfqsByTransportTypeData}
                                    options={chartOptions("doughnut", (label) => addFilter("product", label))}
                                />
                            </Card>
                        </Grid>
                        {/* Top Origins */}
                        <Grid item xs={12} md={4}>
                            <Card sx={{ p: 3, height: 350, borderRadius: "10px", boxShadow: "none" }}>
                                <MDTypography variant="h6" sx={{ mb: 2 }}>
                                    Top Origins
                                </MDTypography>
                                <Doughnut
                                    data={{
                                        labels: topOrigins.map((x) => x.label),
                                        datasets: [{ label: "Quotes", data: topOrigins.map((x) => x.count), borderWidth: 2 }],
                                    }}
                                    options={chartOptions("doughnut", (label) => addFilter("origin", label))}
                                />
                            </Card>
                        </Grid>

                        {/* Top Destinations */}
                        <Grid item xs={12} md={4}>
                            <Card sx={{ p: 3, height: 350, borderRadius: "10px", boxShadow: "none" }}>
                                <MDTypography variant="h6" sx={{ mb: 2 }}>
                                    Top Destinations
                                </MDTypography>
                                <Doughnut
                                    data={{
                                        labels: topDestinations.map((x) => x.label),
                                        datasets: [{ label: "Quotes", data: topDestinations.map((x) => x.count), borderWidth: 2 }],
                                    }}
                                    options={chartOptions("doughnut", (label) => addFilter("destination", label))}
                                />
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Card sx={{ p: 3, height: 350, borderRadius: "10px", boxShadow: "none" }}>
                                <MDTypography variant="h6" sx={{ mb: 2 }}>
                                    Quotes by Service
                                </MDTypography>

                                <Doughnut
                                    data={rfqsByServiceData}
                                    options={chartOptions("doughnut", (label) => addFilter("service", label))}
                                />
                            </Card>
                        </Grid>

                        {/* Requested Month (line) */}
                        <Grid item xs={12} md={6}>
                            <Card
                                sx={{
                                    p: 3,
                                    height: 360,
                                    borderRadius: "10px",
                                    boxShadow: "none",
                                    overflow: "hidden",
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                <MDTypography variant="h6" sx={{ mb: 1 }}>
                                    Quotes by Month
                                </MDTypography>

                                <Box sx={{ flex: 1, minHeight: 0 }}>
                                    <Line
                                        data={{
                                            labels: requestedMonths.map((m) => m.label),
                                            datasets: [
                                                {
                                                    label: "Quotes",
                                                    data: requestedMonths.map((m) => m.count),
                                                    tension: 0.35,
                                                    borderWidth: 3,
                                                    fill: false,
                                                },
                                            ],
                                        }}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: { legend: { display: false } },
                                            scales: {
                                                x: {
                                                    grid: { display: false },
                                                    ticks: { autoSkip: true, maxTicksLimit: 8, maxRotation: 45, minRotation: 45 },
                                                },
                                                y: { grid: { color: "#eee" }, ticks: { stepSize: 1 } },
                                            },
                                            onClick: (e, elements, chart) => {
                                                if (!elements?.length) return;
                                                const idx = elements[0].index;
                                                const label = chart.data.labels[idx];
                                                if (label) addFilter("requestedMonth", label);
                                            },
                                        }}
                                    />
                                </Box>
                            </Card>
                        </Grid>

                        {/* Quotes by Week (line) */}
                        <Grid item xs={12} md={6}>
                            <Card
                                sx={{
                                    p: 3,
                                    height: 360,
                                    borderRadius: "10px",
                                    boxShadow: "none",
                                    overflow: "hidden",
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                <MDTypography variant="h6" sx={{ mb: 1 }}>
                                    Quotes by Week
                                </MDTypography>

                                <Box sx={{ flex: 1, minHeight: 0 }}>
                                    <Line
                                        data={{
                                            labels: requestedWeeks.map((w) => w.label),
                                            datasets: [
                                                {
                                                    label: "Quotes",
                                                    data: requestedWeeks.map((w) => w.count),
                                                    tension: 0.35,
                                                    borderWidth: 3,
                                                    fill: false,
                                                },
                                            ],
                                        }}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: { legend: { display: false } },
                                            scales: {
                                                x: {
                                                    grid: { display: false },
                                                    ticks: { autoSkip: true, maxTicksLimit: 10, maxRotation: 45, minRotation: 45 },
                                                },
                                                y: { grid: { color: "#eee" }, ticks: { stepSize: 1 } },
                                            },
                                            onClick: (e, elements, chart) => {
                                                if (!elements?.length) return;
                                                const idx = elements[0].index;
                                                const label = chart.data.labels[idx];
                                                if (label) addFilter("requestedWeek", label);
                                            },
                                        }}
                                    />
                                </Box>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Card
                                sx={{
                                    p: 3,
                                    height: 360,
                                    borderRadius: "10px",
                                    boxShadow: "none",
                                    overflow: "hidden",
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                <MDTypography variant="h6" sx={{ mb: 1 }}>
                                    Monthly Financial Trend
                                </MDTypography>

                                <Box sx={{ flex: 1, minHeight: 0 }}>
                                    <Line
                                        data={{
                                            labels: monthlyTotals.map((m) => m.label),
                                            datasets: [
                                                {
                                                    label: `Total Selling (${selectedCurrency})`,
                                                    data: monthlyTotals.map((m) => m.sell),
                                                    tension: 0.35,
                                                    borderWidth: 3,
                                                    fill: false,
                                                },
                                                {
                                                    label: `Total Buying (${selectedCurrency})`,
                                                    data: monthlyTotals.map((m) => m.cost),
                                                    tension: 0.35,
                                                    borderWidth: 3,
                                                    fill: false,
                                                },
                                                {
                                                    label: `Profit (${selectedCurrency})`,
                                                    data: monthlyTotals.map((m) => m.profit),
                                                    tension: 0.35,
                                                    borderWidth: 3,
                                                    fill: false,
                                                },
                                            ],
                                        }}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: { legend: { display: true, position: "bottom" } },
                                            scales: {
                                                x: {
                                                    grid: { display: false },
                                                    ticks: { autoSkip: true, maxTicksLimit: 8, maxRotation: 45, minRotation: 45 },
                                                },
                                                y: {
                                                    grid: { color: "#eee" },
                                                    ticks: {
                                                        callback: (v) => fmt2(v),
                                                    },
                                                },
                                            },
                                            onClick: (e, elements, chart) => {
                                                if (!elements?.length) return;
                                                const idx = elements[0].index;
                                                const label = chart.data.labels[idx];
                                                if (label) addFilter("requestedMonth", label);
                                            },
                                        }}
                                    />

                                </Box>
                            </Card>
                        </Grid>
                        {/* Top Customers (bar) */}
                        <Grid item xs={12} md={6}>
                            <Card
                                sx={{
                                    p: 3,
                                    height: 360,
                                    borderRadius: "10px",
                                    boxShadow: "none",
                                    overflow: "hidden",
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                <MDTypography variant="h6" sx={{ mb: 2 }}>
                                    Top Customers (Quote Count)
                                </MDTypography>

                                <Box sx={{ flex: 1, minHeight: 0 }}>
                                    <Bar
                                        data={{
                                            labels: topCustomers.map((c) => c.label),
                                            datasets: [{ label: "Quotes", data: topCustomers.map((c) => c.count), borderRadius: 8 }],
                                        }}
                                        options={{
                                            indexAxis: "y",
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: { legend: { display: false } },
                                            scales: {
                                                x: { grid: { color: "#eee" }, ticks: { stepSize: 1 } },
                                                y: {
                                                    grid: { display: false },
                                                    ticks: {
                                                        autoSkip: true,
                                                        maxTicksLimit: 6,
                                                        callback: function (value) {
                                                            const label = this.getLabelForValue(value);
                                                            return String(label).length > 30 ? String(label).slice(0, 30) + "…" : label;
                                                        },
                                                    },
                                                },
                                            },
                                            onClick: (e, elements, chart) => {
                                                if (!elements?.length) return;
                                                const idx = elements[0].index;
                                                const label = chart.data.labels[idx];
                                                if (label) addFilter("customer", label);
                                            },
                                        }}
                                    />
                                </Box>
                            </Card>
                        </Grid>

                        {/* TABLE AREA (filters + header bar like your example) */}
                        <Grid item xs={12}>
                            <Card sx={{ p: 3, borderRadius: "10px", boxShadow: "none" }}>
                                {/* Filter chips */}
                                {chartFilters.length > 0 && (
                                    <Box mb={2} display="flex" flexWrap="wrap" gap={1} alignItems="center">
                                        {chartFilters
                                            .filter((f) => f?.type && f?.value)
                                            .map((f, idx) => (
                                                <Chip
                                                    key={`${f.type}-${f.value}-${idx}`}
                                                    label={`${f.type}: ${f.value}`}
                                                    onDelete={() => removeFilter(f)}
                                                    color={safeChipColor(chipColor(f.type))}
                                                    size="small"
                                                    icon={<SearchIcon />}
                                                    sx={{ borderRadius: 2, fontWeight: 700 }}
                                                />
                                            ))}

                                        <Button
                                            size="small"
                                            onClick={() => setChartFilters([])}
                                            sx={{ textTransform: "none", fontWeight: 700 }}
                                        >
                                            Clear All
                                        </Button>
                                    </Box>
                                )}

                                {/* Header bar */}
                                <MDBox
                                    display="flex"
                                    justifyContent="space-between"
                                    mb={2}
                                    alignItems="center"
                                    flexWrap="wrap"
                                    gap={2}
                                >
                                    <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                                        <MDTypography variant="h6" sx={{ fontWeight: 700 }}>
                                            Quote List
                                        </MDTypography>
                                    </Box>

                                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                        {/* Currency */}
                                        <FormControl
                                            size="small"
                                            sx={{
                                                width: 200,
                                                "& .MuiInputLabel-root": { fontWeight: 700 },
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: 2,
                                                    height: 55,
                                                    width: 200,
                                                    fontWeight: 700,
                                                    alignItems: "center",
                                                },
                                                "& .MuiSelect-select": {
                                                    height: "55px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    paddingTop: 0,
                                                    paddingBottom: 0,
                                                },
                                            }}
                                        >
                                            <InputLabel id="currency-label">Currency</InputLabel>
                                            <Select
                                                labelId="currency-label"
                                                value={selectedCurrency}
                                                label="Currency"
                                                onChange={(e) => setSelectedCurrency(e.target.value)}
                                                sx={{ width: 200, height: 55 }}
                                                renderValue={(val) => (
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <CurrencyExchangeIcon fontSize="small" />
                                                        <span style={{ fontWeight: 700, letterSpacing: 0.5 }}>{val}</span>
                                                    </Box>
                                                )}
                                            >
                                                <MenuItem value="USD">USD</MenuItem>
                                                <MenuItem value="EUR">EUR</MenuItem>
                                                <MenuItem value="GBP">GBP</MenuItem>
                                                <MenuItem value="TRY">TRY</MenuItem>
                                            </Select>
                                        </FormControl>

                                        {/* Date Start */}
                                        <TextField
                                            size="small"
                                            label="Date Start"
                                            type="date"
                                            value={dateStart}
                                            onChange={(e) => setDateStart(e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            sx={{ minWidth: 170, "& .MuiOutlinedInput-root": { borderRadius: 2, height: 46 } }}
                                        />

                                        {/* Date End */}
                                        <TextField
                                            size="small"
                                            label="Date End"
                                            type="date"
                                            value={dateEnd}
                                            onChange={(e) => setDateEnd(e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            sx={{ minWidth: 170, "& .MuiOutlinedInput-root": { borderRadius: 2, height: 46 } }}
                                        />

                                        {/* Search */}
                                        <TextField
                                            size="small"
                                            placeholder="Search..."
                                            value={globalSearch}
                                            onChange={(e) => setGlobalSearch(e.target.value)}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{ minWidth: 220, "& .MuiOutlinedInput-root": { borderRadius: 2, height: 46 } }}
                                        />

                                        {(dateStart || dateEnd) && (
                                            <Button
                                                size="small"
                                                onClick={() => {
                                                    setDateStart("");
                                                    setDateEnd("");
                                                }}
                                                sx={{ textTransform: "none", fontWeight: 700 }}
                                            >
                                                Clear Dates
                                            </Button>
                                        )}
                                    </Box>
                                </MDBox>

                                <DataTable
                                    table={{
                                        columns,
                                        rows: tableRows,
                                        initialState: { sortBy: [{ id: "quoteDateStr", desc: true }] },
                                    }}
                                    entriesPerPage={{ defaultValue: 100, entries: [10, 25, 50, 100] }}
                                    showTotalEntries
                                    isSorted
                                    sx={{
                                        "& thead": { position: "sticky", top: 0, zIndex: 3 },
                                        "& thead tr th": {
                                            backgroundColor: "#fafafa",
                                            borderBottom: "1px solid rgba(0,0,0,0.06)",
                                        },
                                        "& .MuiTableCell-root": {
                                            whiteSpace: "nowrap",
                                            textOverflow: "ellipsis",
                                            overflow: "hidden",
                                            maxWidth: 240,
                                        },
                                        "& tbody tr:hover": { backgroundColor: "#f7f9fb" },
                                    }}
                                />
                            </Card>
                        </Grid>
                    </Grid>
                )}
            </MDBox>
        </MDBox>
    );

    if (embedded) {
        return content;
    }


    return (
        <DashboardLayout>
            <DashboardNavbar />
            {content}
            <Footer />
        </DashboardLayout>
    );
}
